import { Injectable, inject } from '@angular/core';
import { FILE_LANE, UploadEngine, type UploadedFile } from '@lite-workspace/uploader-core';
import { DesignPackageSpreadCategoryEnum, type DesignPackageSpread, type SpreadCreate } from '@lite-workspace/dto-design-system';
import { DesignsApiService } from './designs-api.service';
import { EmptyUploadAnswerError, TemplateUploadError } from '../entities/template-upload-failure';

/** One freshly created spread, as returned by the server. */
export interface CreatedSpread {
  readonly spread: DesignPackageSpread;
}

interface UploadedAsset {
  readonly fileId: string;
  readonly url: string;
}

/**
 * Ports the legacy fc-designs PSD import flow into the live app: upload the PSD as an asset, then create
 * the spread server-side. The server renders the spread previews (`model.previewVersions`) when it parses
 * the file — the client-side preview extraction was removed, so ag-psd no longer ships in the app bundle.
 * The parsed `model.boxes` and previews arrive later over WS / poll (wired separately). Kept in the app
 * (not imported from `@lite-workspace/fc-designs/domain`) so it does not drag the legacy UI graph.
 */
@Injectable({ providedIn: 'root' })
export class PsdUploadService {
  private readonly engine = inject(UploadEngine);
  private readonly api = inject(DesignsApiService);

  /** Upload one PSD and create its spread; the server-side parse produces the previews. */
  async importPsd(designId: string, file: File): Promise<CreatedSpread> {
    const asset = await this.uploadAsset(designId, file);
    const create: SpreadCreate = {
      name: file.name,
      fileId: asset.fileId,
      category: categoryByFileName(file.name),
    };
    const [spread] = await spreadStage(file, () => this.api.createSpreads(designId, [create]));
    return { spread };
  }

  /** Same-name overwrite: the EXISTING spread takes the new file — identity, category and links survive. */
  async replacePsd(designId: string, spreadId: string, file: File): Promise<CreatedSpread> {
    const asset = await this.uploadAsset(designId, file);
    const spread = await spreadStage(file, () => this.api.replaceSpreadFile(designId, spreadId, { fileId: asset.fileId }));
    return { spread };
  }

  private async uploadAsset(designId: string, file: File): Promise<UploadedAsset> {
    // Attempts MADE, not retries: the queue reports the retry number it schedules. Bytes are the last
    // attempt's — a retry starts the transfer over, so the previous attempt's count is reset with it.
    let bytesSent = 0;
    let attempts = 1;
    let stored: UploadedFile | null;
    try {
      stored = await this.engine.uploadAndWait({
        lane: FILE_LANE,
        key: designId,
        file,
        meta: { projectId: designId, fileId: crypto.randomUUID(), uploadProcess: 'asset' },
        onProgress: (_percent, loaded) => {
          bytesSent = loaded;
        },
        onWaiting: wait => {
          attempts = wait.attempts + 1;
          bytesSent = 0;
        },
      });
    } catch (error) {
      throw new TemplateUploadError('upload', error, file, bytesSent, attempts);
    }

    if (!stored) throw new TemplateUploadError('upload', new EmptyUploadAnswerError(), file, bytesSent, attempts);
    return { fileId: stored.id, url: stored.url ?? '' };
  }
}

// Only the name is read here; an ordinary name stays DEFAULT and the parser's slot-count preset still applies.
function categoryByFileName(name: string): DesignPackageSpreadCategoryEnum {
  if (/^(cover|обложк)/i.test(name)) return DesignPackageSpreadCategoryEnum.COVER;
  if (/^(vignette|виньетк)/i.test(name)) return DesignPackageSpreadCategoryEnum.VIGNETTES;
  return DesignPackageSpreadCategoryEnum.DEFAULT;
}

async function spreadStage<T>(file: File, create: () => Promise<T>): Promise<T> {
  try {
    return await create();
  } catch (error) {
    throw new TemplateUploadError('create_spread', error, file);
  }
}
